import type { CompositePrimaryKey, FieldFilter, FilterRelationalQuantifier as FilterRelationalQuantifierType, GlobalSingleRelationFieldId, ResolvedMonospaceCollection, ResolvedMonospacePrimitiveField } from '#layers/data-model/public';
import type { FilterSchemaCollection, FilterSchemaField } from '#layers/filters/components/Filter/utils/filter-schema';
import type { RelatedItemOperator } from '#layers/filters/components/Filter/utils/related-item-match';
import { isNonArrayRecord } from '#layers/base/public';
import { FilterOperator, FilterRelationalQuantifier, getCollectionPrimaryKeyFields, isM2M } from '#layers/data-model/public';
import { nestFilterThroughRelations } from '#layers/filters/components/Filter/utils/filter-path';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { buildTerminalMatch, isExclusionOperator, negateOperator, parseTerminalMatch } from '#layers/filters/components/Filter/utils/related-item-match';
import { useWorkspaceSchemaStore } from '#layers/workspace/public';

// `_every` (a subset match) is intentionally excluded: it reads as a footgun in an identity picker, so
// an `_every` filter falls through to the generic field editor rather than rendering as a related-item pill.
const TO_MANY_QUANTIFIERS = new Set<string>([
	FilterRelationalQuantifier.Some,
	FilterRelationalQuantifier.None,
]);

export interface RelatedItemClause {
	relationFieldPath: GlobalSingleRelationFieldId[];
	oppositeCollection: ResolvedMonospaceCollection;
	primaryKeyFields: ResolvedMonospacePrimitiveField[];
	operator: RelatedItemOperator;
	keys: CompositePrimaryKey[];
	traversesToMany: boolean;
	displayFieldPath: GlobalSingleRelationFieldId[];
	displayFieldPathRealIndices: number[];
}

function isRelationField(field: FilterSchemaField): field is Extract<FilterSchemaField, { isList: boolean }> {
	return !('type' in field);
}

function isPositiveMembership(operator: RelatedItemOperator): boolean {
	return operator === FilterOperator.EQ || operator === FilterOperator.IN;
}

export function useRelatedItemMatch() {
	const schema = injectFilterSchemaContext();
	const schemaStore = useWorkspaceSchemaStore();

	// The real indices of the hops kept in the display path
	function displayHopRealIndices(path: GlobalSingleRelationFieldId[]): number[] {
		return path.reduce<number[]>((indices, _id, index) => {
			const previous = index === 0 ? undefined : schemaStore.findSingleRelationFieldById(path[index - 1]!);
			if (index === 0 || !(previous && isM2M(previous))) indices.push(index);
			return indices;
		}, []);
	}

	function parseRelatedItemClause(filter: FieldFilter, collection: FilterSchemaCollection): RelatedItemClause | null {
		const relationFieldPath: GlobalSingleRelationFieldId[] = [];
		let currentCollection: FilterSchemaCollection = collection;
		let currentObj: unknown = filter;
		let traversesToMany = false;
		let deepestQuantifier: FilterRelationalQuantifierType | undefined;

		while (isNonArrayRecord(currentObj) && Object.keys(currentObj).length === 1) {
			const apiName = Object.keys(currentObj)[0]!;
			const field = schema.findFieldByApiName(currentCollection, apiName);

			// A primitive first key (or a logical `_or`/`_not`) is the terminal match, not a relation hop.
			if (!field || !isRelationField(field)) break;

			let inner: unknown = currentObj[apiName];

			if (field.isList) {
				// A to-many hop wraps its inner match in a single relational quantifier (`_some`/`_none`);
				// polarity lives here, so the terminal below stays positive.
				if (!isNonArrayRecord(inner) || Object.keys(inner).length !== 1) break;

				const quantifier = Object.keys(inner)[0]!;
				if (!TO_MANY_QUANTIFIERS.has(quantifier)) break;

				if (traversesToMany && deepestQuantifier !== FilterRelationalQuantifier.Some) return null;

				traversesToMany = true;
				deepestQuantifier = quantifier as FilterRelationalQuantifierType;
				inner = inner[quantifier];
			}

			const next = schema.findOppositeCollectionByRelationFieldId(field.id);
			if (!next) return null;

			relationFieldPath.push(field.id);
			currentCollection = next;
			currentObj = inner;
		}

		if (relationFieldPath.length === 0) return null;

		const oppositeCollection = schemaStore.findSingleRelationFieldById(relationFieldPath.at(-1)!)?.oppositeCollection;
		if (!oppositeCollection) return null;

		const primaryKeyFields = getCollectionPrimaryKeyFields(oppositeCollection);
		if (primaryKeyFields.length === 0) return null;

		const displayFieldPathRealIndices = displayHopRealIndices(relationFieldPath);
		const displayFieldPath = displayFieldPathRealIndices.map((index) => relationFieldPath[index]!);

		// A cleared selection serializes to an empty terminal (`{ relation: {} }` / `{ relation: { _some: {} } }`).
		// Keep it as an inert related-item clause with no keys so the picker row still renders (the empty
		// terminal is pruned at query time by `toApplicableFilter`).
		if (isNonArrayRecord(currentObj) && Object.keys(currentObj).length === 0) {
			const isExclusion = deepestQuantifier === FilterRelationalQuantifier.None;

			return {
				relationFieldPath,
				displayFieldPath,
				displayFieldPathRealIndices,
				oppositeCollection,
				primaryKeyFields,
				operator: traversesToMany ? (isExclusion ? FilterOperator.NIN : FilterOperator.IN) : FilterOperator.EQ,
				keys: [],
				traversesToMany,
			};
		}

		const terminal = parseTerminalMatch(currentObj, primaryKeyFields);
		if (!terminal) return null;

		if (traversesToMany) {
			if (!isPositiveMembership(terminal.operator)) return null;

			const operator = deepestQuantifier === FilterRelationalQuantifier.None ? negateOperator(terminal.operator) : terminal.operator;

			return { relationFieldPath, displayFieldPath, displayFieldPathRealIndices, oppositeCollection, primaryKeyFields, operator, keys: terminal.keys, traversesToMany };
		}

		return {
			relationFieldPath,
			displayFieldPath,
			displayFieldPathRealIndices,
			oppositeCollection,
			primaryKeyFields,
			operator: terminal.operator,
			keys: terminal.keys,
			traversesToMany,
		};
	}

	function isRelatedItemMatch(filter: FieldFilter, collection: FilterSchemaCollection): boolean {
		return parseRelatedItemClause(filter, collection) !== null;
	}

	function serializeRelatedItemClause(clause: Pick<RelatedItemClause, 'relationFieldPath' | 'operator' | 'keys' | 'primaryKeyFields'>): FieldFilter {
		const traversesToMany = clause.relationFieldPath.some((id) => schema.findSingleRelationFieldById(id)?.isList);
		const isExclusion = isExclusionOperator(clause.operator);

		// A to-many match carries polarity via its quantifier (`_some`/`_none`) around a positive membership
		// terminal; a pure to-one match carries it inline (`_neq`/`_nin`). An M2M junction hop is an
		// ordinary hop in `relationFieldPath`, so nesting reproduces `source → junction → target`.
		const terminal = buildTerminalMatch(clause.operator, clause.keys, clause.primaryKeyFields, { positive: traversesToMany });
		const options = traversesToMany ? { quantifier: isExclusion ? FilterRelationalQuantifier.None : FilterRelationalQuantifier.Some } : {};
		const condition = nestFilterThroughRelations(clause.relationFieldPath, terminal, (id) => schema.findSingleRelationFieldById(id), options);

		if (!condition) throw new Error(`[serializeRelatedItemClause] relation not found in path: ${clause.relationFieldPath.join(', ')}`);

		return condition as FieldFilter;
	}

	return { parseRelatedItemClause, serializeRelatedItemClause, isRelatedItemMatch };
}
